import { createReducer } from "@reduxjs/toolkit";
import { ComplaintSettings } from "src/models/complaint.settings";
import {
  ChangeComplaintCount,
  DeleteComplaintSuccess,
  GetComplaintList,
  GetComplaintsCount,
  GetComplaintSettings,
  GetComplaintSettingSuccess,
  GetComplaintSupplierList,
  SaveAndEditContinue,
  SetComplaintGeneralSuccess,
  SetComplaintType,
  UpdateComplaintGeneral,
} from "./complaint.action";

export interface ComplaintCount {
  overdue: number;
  resolved: number;
  outstanding: number;
  deleted: number;
}

export interface IComplaintState {
  list: any[];
  dataCount: number;
  isLoadingList: boolean;
  complaintSettings: ComplaintSettings;
  complaintGeneral: any;
  complaintsCount: {
    [type: string]: Partial<ComplaintCount>;
  };
  IsSaveAndEditContinue: boolean;
  complaintType: any;
}

const initialState: Partial<IComplaintState> = {
  list: [],
  dataCount: 0,
  complaintsCount: {},
};

export const complaintReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(GetComplaintList.pending, (state, action) => {
      state = {
        ...state,
        isLoadingList: true,
      };
      return state;
    })

    .addCase(GetComplaintList.fulfilled, (state, action) => {
      state = {
        ...state,
        list: action.payload?.data,
        dataCount: action.payload?.dataCount,
        isLoadingList: false,
      };
      return state;
    })
    .addCase(GetComplaintSupplierList.fulfilled, (state, action) => {
      state = {
        ...state,
        list: action.payload?.data,
        dataCount: action.payload?.dataCount,
        isLoadingList: false,
      };
      return state;
    })
    .addCase(SetComplaintType, (state, action) => {
      state = {
        ...state,
        complaintType: action.payload,
      };
      return state;
    })
    .addCase(GetComplaintSettings.fulfilled, (state, action) => {
      state = {
        ...state,
        complaintSettings: action.payload,
      };
      return state;
    })
    .addCase(SaveAndEditContinue, (state, action) => {
      state = {
        ...state,
        IsSaveAndEditContinue: action.payload,
      };
      return state;
    })
    .addCase(GetComplaintSettingSuccess, (state, action) => {
      state = {
        ...state,
        complaintSettings: action.payload,
      };
      return state;
    })
    .addCase(SetComplaintGeneralSuccess, (state, action) => {
      state = {
        ...state,
        complaintGeneral: action.payload,
      };
      return state;
    })
    .addCase(DeleteComplaintSuccess, (state, action) => {
      const complaintType = state.complaintType;
      const listBefore = state.list || [];
      const filtered = listBefore.filter((item) => {
        return !action.payload?.includes(item.id.toString());
      });

      const deleteCount = action.payload?.length || 0;
      const deleteType = countDeleteRows(action.payload, listBefore);

      state.list = filtered;
      state.dataCount = (state.dataCount || 0) - deleteCount;

      if (state.complaintsCount && state.complaintsCount[complaintType]) {
        const currentCount = state.complaintsCount[complaintType];

        state.complaintsCount[complaintType] = {
          overdue: (currentCount.overdue || 0) - deleteType.overdue,
          outstanding:
            (currentCount.outstanding || 0) -
            deleteType.outstanding -
            deleteType.overdue,
          resolved: (currentCount.resolved || 0) - deleteType.resolved,
          deleted: (currentCount.deleted || 0) + deleteCount,
        };
      }

      return state;
    })

    .addCase(UpdateComplaintGeneral, (state, action) => {
      let data = {
        ...state.complaintGeneral,
        ...action.payload,
      };

      state = {
        ...state,
        complaintGeneral: data,
      };
      return state;
    })
    .addCase(GetComplaintsCount.fulfilled, (state, action) => {
      const type = action.meta.arg;

      if (!state.complaintsCount) state.complaintsCount = {};
      state.complaintsCount[type] = {
        overdue: action?.payload?.overdueCount ?? 0,
        outstanding: action?.payload?.outstandingCount ?? 0,
        resolved: action?.payload?.resolvedCount ?? 0,
        deleted: action?.payload?.deletedCount ?? 0,
      };
      return state;
    })
    .addCase(ChangeComplaintCount, (state, action) => {
      const payload = action.payload as any;
      if (payload?.type) {
        const { type, ...counts } = payload;
        if (!state.complaintsCount) state.complaintsCount = {};
        state.complaintsCount[type] = {
          ...state.complaintsCount[type],
          ...counts,
        };
        return state;
      }
      for (const type in payload) {
        state.complaintsCount[type] = {
          ...state.complaintsCount[type],
          ...payload[type],
        };
      }

      return state;
    });
});

function countDeleteRows(ids: string[], state: any[]): any {
  let deletedRows = state.filter((item) => ids.includes(item?.id?.toString()));

  let deleteCount: any = deletedRows.reduce(
    (total, currentValue) => {
      return {
        outstanding:
          total.outstanding +
          ((currentValue.status as string).toLowerCase() === "outstanding"
            ? 1
            : 0),
        resolved:
          total.resolved +
          ((currentValue.status as string).toLowerCase() === "resolved"
            ? 1
            : 0),
        overdue:
          total.overdue +
          ((currentValue.status as string).toLowerCase() === "overdue" ? 1 : 0),
      };
    },
    {
      outstanding: 0,
      resolved: 0,
      overdue: 0,
    }
  );

  return deleteCount;
}
